<!-- eslint-disable vue/no-unused-refs -->
 <!-- eslint-disable vuejs-accessibility/no-static-element-interactions -->
<template>
  <form
    class="search-bar"
    @submit.prevent="handleFormSubmit"
    @click="handleClick"
  >
    <label
      class="visually-hidden"
      :for="`search-${namespace}`"
      v-text="$string('general.site_header.search.title')"
    />

    <input
      :id="`search-${namespace}`"
      ref="input"
      v-model="input"
      class="search-bar__input text-body-m-bold text-body-s-bold-desktop"
      :class="{ [$class('active')]: searchQuery !== '' }"
      :placeholder="$string('general.site_header.search.placeholder')"
      type="search"
      enterkeyhint="search"
      @focus="handleFocus"
    >

    <button
      class="search-bar__submit icon-button"
      type="submit"
      @blur="handleButtonBlur"
    >
      <span
        class="visually-hidden"
        v-text="$string('general.site_header.search.submit')"
      />

      <icon-search />
    </button>

    <button
      v-if="searchQuery !== ''"
      class="search-bar__clear text-body-s-underline"
      type="button"
      @click.prevent="handleClear"
      v-text="$string('search.clear')"
    />

    <button
      v-if="isOverlayActive"
      class="search-bar__clear icon-button"
      type="button"
      @click.stop="handleClose"
    >
      <span
        class="visually-hidden"
        v-text="$string('search.clear')"
      />

      <icon-close />
    </button>
  </form>
</template>

<script>

/**
 * Vue: Search bar (search-bar)
 * -----------------------------------------------------------------------------
 * Search from snippet used in site header.
 * - Component settings: snippet / dynamic / global
 *
 * @param {String} namespace - Unique identifier.
 * @param {Object} redirect - Redirect URL.
 * @param {String} searchQuery - Current search query, used to update input
 * value when clicking suggestion.
 * @param {Boolean} isOverlayActive - Show/hide close button.
 *
 */
import { mapState } from 'vuex'

import IconClose from '~icons/directional-navigation/close.svg'
import IconSearch from '~icons/general/search.svg'
import { initInsights, sendSearchQueryEvent } from '~/helpers/algolia-insights'

export default {
  name: 'SearchBar',

  components: {
    IconSearch,
    IconClose,
  },

  props: {
    namespace: {
      type: String,
      default: '',
      required: true,
    },
    redirect: {
      type: Boolean,
      default: false,
    },
    searchQuery: {
      type: String,
      default: '',
    },
    isOverlayActive: {
      type: Boolean,
      default: false,
    },
  },

  emits: {
    'search-focus': (payload) => typeof payload === 'boolean',
    'search-click': (payload) => typeof payload === 'boolean',
    'search-input': (payload) => typeof payload === 'string',
    'search-submit': () => true,
    clear: () => true,
    close: () => true,
  },

  data() {
    return {
      input: '',
      query: '',
      timeout: '',
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      tabbable: (state) => state.accessibility.tabbable,
    }),
  },

  watch: {

    /**
     * Watch input to debounce update query.
     * - Debounces input to set query only when finished typing.
     * - Don't watch query to emit event because search query can update query
     *   when user clicks on a suggestion, don't want to trigger two updates.
     */
    input(query) {
      if (this.timeout) {
        clearTimeout(this.timeout)
      }

      this.timeout = setTimeout(() => {
        this.query = query
        this.$emit('search-input', this.query)
      }, this.$timing('normal'))
    },

    /**
     * Watch search query to update query.
     */
    searchQuery() {
      this.input = this.searchQuery
    },
  },

  mounted() {
    this.input = new URLSearchParams(window.location.search).get('q') || this.input
  },

  methods: {

    /**
     * Handle click to emit event.
     */
    handleClick() {
      this.$emit('search-click', true)
    },

    /**
     * Handle focus to emit event.
     */
    handleFocus() {
      this.$emit('search-focus', true)
    },

    /**
     * Handle button blur.
     * - Assumes that if tabbable is active, there is no query, and button blurs
     *   then the user wants to tab outside the search bar so predictive search
     *   should close.
     */
    handleButtonBlur() {
      if (!this.tabbable || this.query) {
        return
      }

      this.$emit('search-focus', false)
    },

    /**
     * Handle form submit.
     * - If redirect is true, redirect to search page with query.
     * - If redirect is false, emit form submit.
     * - Track search query submission event.
     */
    handleFormSubmit() {
      if (!this.input) {
        this.$refs.input.focus()
        this.handleClick()
        return
      }

      // Track search query event on submit
      initInsights()
      const indexName = window.cnvs?.algolia?.indices?.products || 'shopify_products'
      sendSearchQueryEvent({ indexName, query: this.input })

      if (!this.redirect) {
        this.$emit('search-submit')
        return
      }

      location.href = `${this.$variable('routes.search')}?q=${this.input}&type=product`
    },

    /**
     * Handle clear to emit event.
     */
    handleClear() {
      this.$emit('clear')
    },

    /**
     * Handle clear to emit event.
     */
    handleClose() {
      this.$emit('close')
    },
  },
}
</script>
